import React from 'react';

/**
 * Breadcrumb Schema Markup Generator
 * Generates JSON-LD structured data for breadcrumb navigation
 */

export interface BreadcrumbItem {
  name: string;
  url: string;
}

/**
 * Canonical host for breadcrumb `item` URLs.
 *
 * schema.org requires `item` to be an absolute URL. This generator passed
 * `item.url` straight through, so callers supplying root-relative paths
 * (/blinds-in/[city] and /guides/measuring both do) emitted `"item": "/"`,
 * `"item": "/guides"` — which identify no resource. Every OTHER BreadcrumbList
 * emitter on the site already absolutizes; these two were the outliers.
 */
const CANONICAL_HOST = 'https://smartblindspro.com';

export function generateBreadcrumbSchema(items: BreadcrumbItem[], host: string = CANONICAL_HOST) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url.startsWith('http') ? item.url : `${host}${item.url}`,
    })),
  };

  return schema;
}

/**
 * Generate Breadcrumb JSON-LD script tag
 */
export function BreadcrumbSchemaScript({ items }: { items: BreadcrumbItem[] }) {
  const schema = generateBreadcrumbSchema(items);

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}
